import { Location } from 'history';

import { ICustomProperties } from '@microsoft/applicationinsights-core-js';
import {
	ApplicationInsights,
	IEventTelemetry,
	IExceptionTelemetry,
	IMetricTelemetry,
} from '@microsoft/applicationinsights-web';

// Test URL: https://local.c3.mepla.microsoft.com/external?link=quote&id=8610306ea39f&referrer=abc

class LogEvent {
	public eventTelemetry: IEventTelemetry;
	public customProperties?: ICustomProperties;

	public constructor(logEvent: LogEvent) {
		this.eventTelemetry = logEvent.eventTelemetry;
		this.customProperties = logEvent.customProperties;
	}
}

class ErrorEvent {
	public exceptionTelemetry: IExceptionTelemetry;

	public constructor(errorEvent: ErrorEvent) {
		this.exceptionTelemetry = errorEvent.exceptionTelemetry;
	}
}

class LogMetric {
	public metricTelemetry: IMetricTelemetry;
	public properties?: ICustomProperties;

	public constructor(logMetric: LogMetric) {
		this.metricTelemetry = logMetric.metricTelemetry;
		this.properties = logMetric.properties;
	}
}

class LogTrace {
	public message: string;
	public properties?: ICustomProperties;
	public severityLevel?: number;

	public constructor(logTrace: LogTrace) {
		this.message = logTrace.message;
		this.properties = logTrace.properties;
		this.severityLevel = logTrace.severityLevel;
	}
}

class PageLoadEvent {
	public pageName: string;
	public fullPathName: string;

	public constructor(pageLoadEvent: PageLoadEvent) {
		this.pageName = pageLoadEvent.pageName;
		this.fullPathName = pageLoadEvent.fullPathName;
	}
}

class LoggerService {
	private static _instance: LoggerService;

	private appInsights: ApplicationInsights | undefined;
	private corpAppInsights: ApplicationInsights | undefined;
	// eslint-disable-next-line @typescript-eslint/array-type
	private eventQueue: Array<LogEvent | ErrorEvent | PageLoadEvent> = [];
	private metricQueue: Array<LogMetric> = [];
	private traceQueue: Array<LogTrace> = [];
	private consoleLog = false;

	public static get Instance() {
		return this._instance || (this._instance = new this());
	}

	public init(appInsights: ApplicationInsights) {
		this.appInsights = appInsights;

		this.eventQueue.forEach(event => {
			if (event instanceof LogEvent) {
				return this.log(event.eventTelemetry, event.customProperties);
			}

			if (event instanceof ErrorEvent) {
				return this.error(event.exceptionTelemetry);
			}

			if (event instanceof PageLoadEvent) {
				return this.pageLoad(event.pageName, event.fullPathName);
			}
		});
		this.metricQueue.forEach(metric => {
			if (metric instanceof LogMetric) {
				return this.trackMetric(
					metric.metricTelemetry.name,
					metric.metricTelemetry.average,
					metric.properties
				);
			}
		});
		this.traceQueue.forEach(trace => {
			if (trace instanceof LogTrace) {
				return this.trackTrace(trace.message, trace.properties, trace.severityLevel);
			}
		});
	}

	public initCorp(corpAppInsights: ApplicationInsights) {
		this.corpAppInsights = corpAppInsights;
	}

	public setUserContext(userId: string) {
		this.appInsights?.setAuthenticatedUserContext(userId);
		try {
			this.corpAppInsights?.setAuthenticatedUserContext(userId);
			this.appInsights?.trackEvent(
				{ name: 'CorpTelemetryDebug' },
				{ method: 'setUserContext', status: 'success' }
			);
		} catch (e) {
			console.error('Failed to set corp authenticated user context, disabling corp telemetry', e);
			this.appInsights?.trackException({
				exception: e instanceof Error ? e : new Error(String(e)),
				properties: { source: 'corpAppInsights.setUserContext' },
			});
			this.corpAppInsights = undefined;
		}
	}

	public log(eventTelemetry: IEventTelemetry, customProperties?: ICustomProperties | undefined) {
		if (this.consoleLog)
			console.log(`${!this.appInsights ? 'QUEUED ' : ''}LoggerService.log`, {
				eventTelemetry,
				customProperties,
			});

		if (!this.appInsights) {
			return this.eventQueue.push(new LogEvent({ eventTelemetry, customProperties }));
		}

		this.appInsights.trackEvent(eventTelemetry, customProperties);
		try {
			this.corpAppInsights?.trackEvent(eventTelemetry, customProperties);
			this.appInsights?.trackEvent(
				{ name: 'CorpTelemetryDebug' },
				{ method: 'trackEvent', status: 'success' }
			);
		} catch (e) {
			console.error('Failed to track corp event, disabling corp telemetry', e);
			this.appInsights?.trackException({
				exception: e instanceof Error ? e : new Error(String(e)),
				properties: { source: 'corpAppInsights.trackEvent' },
			});
			this.corpAppInsights = undefined;
		}
	}

	public error(exceptionTelemetry: IExceptionTelemetry) {
		if (this.consoleLog)
			console.log(`${!this.appInsights ? 'QUEUED ' : ''}  Service.error`, {
				exceptionTelemetry,
			});

		if (!this.appInsights) {
			return this.eventQueue.push(new ErrorEvent({ exceptionTelemetry }));
		}

		this.appInsights.trackException(exceptionTelemetry);
		try {
			this.corpAppInsights?.trackException(exceptionTelemetry);
			this.appInsights?.trackEvent(
				{ name: 'CorpTelemetryDebug' },
				{ method: 'trackException', status: 'success' }
			);
		} catch (e) {
			console.error('Failed to track corp exception, disabling corp telemetry', e);
			this.appInsights?.trackException({
				exception: e instanceof Error ? e : new Error(String(e)),
				properties: { source: 'corpAppInsights.trackException' },
			});
			this.corpAppInsights = undefined;
		}
	}

	/**
	 * Log to App Insights customMetrics table
	 * @param name - Metric name (used as identifier in App Insights)
	 * @param value - Numeric value for the metric (e.g., duration, count)
	 * @param properties - Additional properties/dimensions for the metric
	 */

	public trackMetric(name: string, value: number, properties?: Record<string, any>) {
		if (this.consoleLog)
			console.log(`${!this.appInsights ? 'QUEUED ' : ''}LoggerService.trackMetric`, {
				name,
				value,
				properties,
			});

		const metricTelemetry: IMetricTelemetry = {
			name,
			average: value,
		};

		if (!this.appInsights) {
			return this.metricQueue.push(new LogMetric({ metricTelemetry, properties }));
		}

		this.appInsights.trackMetric(metricTelemetry, properties);
		try {
			this.corpAppInsights?.trackMetric(metricTelemetry, properties);
			this.appInsights?.trackEvent(
				{ name: 'CorpTelemetryDebug' },
				{ method: 'trackMetric', status: 'success' }
			);
		} catch (e) {
			console.error('Failed to track corp metric, disabling corp telemetry', e);
			this.appInsights?.trackException({
				exception: e instanceof Error ? e : new Error(String(e)),
				properties: { source: 'corpAppInsights.trackMetric' },
			});
			this.corpAppInsights = undefined;
		}
	}

	/**
	 * Log to App Insights traces table
	 * @param message - Trace message (used as identifier in App Insights)
	 * @param properties - Additional properties for the trace
	 * @param severityLevel - Optional severity level (0=Verbose, 1=Information, 2=Warning, 3=Error, 4=Critical)
	 */
	public trackTrace(message: string, properties?: Record<string, any>, severityLevel?: number) {
		if (this.consoleLog)
			console.log(`${!this.appInsights ? 'QUEUED ' : ''}LoggerService.trackTrace`, {
				message,
				properties,
				severityLevel,
			});

		if (!this.appInsights) {
			return this.traceQueue.push(new LogTrace({ message, properties, severityLevel }));
		}

		this.appInsights.trackTrace(
			{
				message,
				severityLevel,
			},
			properties
		);
		try {
			this.corpAppInsights?.trackTrace(
				{
					message,
					severityLevel,
				},
				properties
			);
			this.appInsights?.trackEvent(
				{ name: 'CorpTelemetryDebug' },
				{ method: 'trackTrace', status: 'success' }
			);
		} catch (e) {
			console.error('Failed to track corp trace, disabling corp telemetry', e);
			this.appInsights?.trackException({
				exception: e instanceof Error ? e : new Error(String(e)),
				properties: { source: 'corpAppInsights.trackTrace' },
			});
			this.corpAppInsights = undefined;
		}
	}

	public locationChange(location: Location) {
		// Rewrite Page Names --------------------------------------------------------------------
		// Removing page id's from the page view name so that AppInsights reporting has less noise
		// and groups all Quote/Customer "page views" together.

		let pageName = location.pathname;

		// Remove id '8610306ea39j' after '/quote/8610306ea39j'
		pageName = pageName.replace(/quote\/[^/]+/g, 'quote');

		// Remove id '99' after '/customer/99'
		pageName = pageName.replace(/customer\/[^/]+/g, 'customer');

		// Remove id 'ngvl:1234567890' after '/customer/agreements/active/ngvl:1234567890/info' or '/customer/agreements/expired/ngvl:1234567890/info'
		pageName = pageName.replace(
			/customer\/agreements\/(active|expired)\/[^/]+/g,
			'customer/agreements/$1'
		);
		// Remove id 'ab1234567890' after /customer/subscriptions/all/ab1234567890
		pageName = pageName.replace(/customer\/subscriptions\/all\/(.*)/, 'customer/subscriptions/all');

		// Skip Location Changes -----------------------------------------------------------------
		// These locations are known to be redirected in React components using <Redirect... />.
		// As such, we want to skip the telemetry logging of these extra steps in redirection to
		// lessen the telemetry noise when evaluating a user's navigation flow/path from page to
		// page during their session.  This somewhat "brittle" approach to ignoring certain location
		// changes was done given the likely changes that will come to React Router in the near
		// future that may affect our approach to this problem.
		switch (pageName) {
			case '/':
			case '/home':
			case '/home/help':
			case '/home/quotes':
			case '/home/referrals':
			case '/quote':
			case '/external':
			case '/customer':
			case '/customer/agreements':
			case '/customer/agreements/active':
			case '/customer/agreements/expired':
			case '/customer/subscriptions':
				return;
		}

		this.pageLoad(pageName, location.pathname);
	}

	public pageLoad(pageName: string, fullPathName: string) {
		if (this.consoleLog)
			console.log(`${!this.appInsights ? 'QUEUED ' : ''}LoggerService.pageLoad`, {
				pageName,
				fullPathName,
			});

		if (!this.appInsights) {
			return this.eventQueue.push(new PageLoadEvent({ pageName, fullPathName }));
		}

		this.appInsights.trackPageView({
			name: pageName,
			properties: { fullPathName },
		});
		try {
			this.corpAppInsights?.trackPageView({
				name: pageName,
				properties: { fullPathName },
			});
			this.appInsights?.trackEvent(
				{ name: 'CorpTelemetryDebug' },
				{ method: 'trackPageView', status: 'success' }
			);
		} catch (e) {
			console.error('Failed to track corp page view, disabling corp telemetry', e);
			this.appInsights?.trackException({
				exception: e instanceof Error ? e : new Error(String(e)),
				properties: { source: 'corpAppInsights.trackPageView' },
			});
			this.corpAppInsights = undefined;
		}
	}

	public getSessionId() {
		return this.appInsights?.context.getSessionId();
	}
}

export default LoggerService.Instance;
